import type { RunSummary } from "./types.js";

const BEST_KEY = "trash-panic:best";
/**
 * Simple-mode best (LOCKED-DESIGN 18.11): kid scores live in their OWN slot so
 * an unranked Simple run never clobbers the adult best labels on title / shift /
 * results. Read/written only when the session is in Simple mode.
 */
const SIMPLE_BEST_KEY = "trash-panic:best-simple";
const HISTORY_KEY = "trash-panic:history";
const HISTORY_MAX = 20;

export type LocalBest = {
  score: number;
  bestCombo: number;
  at: string;
};

export type LocalRun = {
  score: number;
  bestCombo: number;
  incinerated: number;
  portalTraversals: number;
  durationMs: number;
  endReason: string;
  at: string;
};

export function getBest(): LocalBest | null {
  try {
    const raw = localStorage.getItem(BEST_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LocalBest;
  } catch {
    return null;
  }
}

export function recordRun(summary: RunSummary): { personalBest: boolean; best: LocalBest } {
  const score =
    summary.metrics.find((m) => m.leaderboardId === "score")?.value ?? 0;
  const bestCombo =
    summary.metrics.find((m) => m.leaderboardId === "combo")?.value ??
    summary.bestChain ??
    0;
  const incinerated =
    typeof summary.metadata?.["incinerated"] === "number"
      ? (summary.metadata["incinerated"] as number)
      : 0;

  const run: LocalRun = {
    score,
    bestCombo,
    incinerated,
    portalTraversals: summary.portalTraversals ?? 0,
    durationMs: summary.durationMs,
    endReason: summary.endReason,
    at: new Date().toISOString(),
  };

  // history
  try {
    const prev = localStorage.getItem(HISTORY_KEY);
    const list: LocalRun[] = prev ? (JSON.parse(prev) as LocalRun[]) : [];
    list.unshift(run);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, HISTORY_MAX)));
  } catch {
    /* ignore */
  }

  const prevBest = getBest();
  // A zero-score or failed first run is NOT a personal best (LOCKED-DESIGN
  // 18.5/18.8 "New best: 0" fix): a best requires actual earned score that
  // beats any prior best. An overflow-ended run with real score can still rank.
  const personalBest = score > 0 && (!prevBest || score > prevBest.score);
  const best: LocalBest = personalBest
    ? { score, bestCombo, at: run.at }
    : prevBest ?? { score: 0, bestCombo, at: run.at };

  if (personalBest) {
    try {
      localStorage.setItem(BEST_KEY, JSON.stringify(best));
    } catch {
      /* ignore */
    }
  }

  return { personalBest, best };
}

export function formatBest(best: LocalBest | null): string {
  // Migrate-on-read: a zero/negative stored best is stale data from the old
  // "New best: 0" bug and never reads as a real high score (LOCKED-DESIGN 18.8).
  if (!best || best.score <= 0) return "No high score yet";
  return `Best: ${best.score.toLocaleString()} · chain ${best.bestCombo}`;
}

// ---------- Simple-mode best (LOCKED-DESIGN 18.11, separate slot) ----------

/** Read the Simple-mode personal best (kid slot; never the adult best). */
export function getSimpleBest(): LocalBest | null {
  try {
    const raw = localStorage.getItem(SIMPLE_BEST_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LocalBest;
  } catch {
    return null;
  }
}

/**
 * Record a Simple-mode run into the kid best slot ONLY. Mirrors recordRun's
 * ">0 beats prior" rule so a failed/overflow run never fakes a best, but writes
 * nothing to the adult BEST_KEY/HISTORY — Simple is unranked and must not
 * pollute the ranked labels (LOCKED-DESIGN 18.11).
 */
export function recordSimpleRun(summary: RunSummary): { personalBest: boolean; best: LocalBest } {
  const score = summary.metrics.find((m) => m.leaderboardId === "score")?.value ?? 0;
  const bestCombo =
    summary.metrics.find((m) => m.leaderboardId === "combo")?.value ?? summary.bestChain ?? 0;
  const prevBest = getSimpleBest();
  const personalBest = score > 0 && (!prevBest || score > prevBest.score);
  const best: LocalBest = personalBest
    ? { score, bestCombo, at: new Date().toISOString() }
    : prevBest ?? { score: 0, bestCombo, at: new Date().toISOString() };
  if (personalBest) {
    try {
      localStorage.setItem(SIMPLE_BEST_KEY, JSON.stringify(best));
    } catch {
      /* ignore */
    }
  }
  return { personalBest, best };
}

/** "YOUR BEST: ‹n›" early-reader line for the Simple results/shell (18.11). */
export function formatSimpleBest(best: LocalBest | null): string {
  if (!best || best.score <= 0) return "";
  return `YOUR BEST: ${best.score.toLocaleString()}`;
}
